import * as React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridFooter,
  palette,
} from "@conserveit/blocks";
import {
  GroupAssets,
  getEquipsCount,
  getHitsCount,
  SplitActioned,
  splitActioned,
} from "../../utils";
import { useGridState } from "../../utils/hooks";
import { useState as useAnalysisState } from "../../../../context/analysis";

const DEFAULT_LOAD_COUNT = 10;

const GoToEquip = styled(Link)`
  color: inherit;
  text-decoration: none;
  display: contents;

  /** CSS hack to add border-top to the first All Associated row */
  &.default-density-row
    + .compact-density-row
    > .DataGrid-Row
    > .DataGrid-Cell {
    border-top: 2px solid ${palette.neutral4};
  }
`;
const StyledDataGridFooter = styled(DataGridFooter)`
  border-width: 1px 0px 0px;
  border-color: initial;
  border-top: 1px solid;
  border-image: linear-gradient(
      270deg,
      rgb(247, 249, 250) 0%,
      rgb(207, 211, 214) 50%,
      rgb(247, 249, 250) 100%
    )
    1 / 1 / 0 stretch;
`;

const Grid = styled(DataGrid)`
  user-select: none;
`;

interface Props {
  groupAssets: GroupAssets;
  occurrences: Array<Insite.Insights.ComposedInsight>;
  filtered: Array<Insite.Insights.ComposedOccurrence>;
  persistKey?: string;
  showSnoozed?: Insite.AppState["insights"]["showSnoozed"];
}

const GroupGrid: React.FunctionComponent<Props> = ({
  groupAssets,
  occurrences,
  filtered,
  persistKey,
  showSnoozed,
}) => {
  if (occurrences.length === 0) {
    return null;
  }

  const { viewMode } = useAnalysisState();

  const {
    sorted,
    currentCount,
    activeSort,
    sortDirection,
    onSort,
    setCurrentCount,
    totalCount,
  } = useGridState(occurrences, filtered, persistKey);
  const [actioned, setActioned] = React.useState<
    SplitActioned<Insite.Insights.ComposedInsight>
  >({ actioned: [], unactioned: [] });

  React.useLayoutEffect(() => {
    if (sorted.length > 0) {
      setActioned(splitActioned(sorted));
    }
  }, [sorted]);

  const rows = React.useMemo(
    () => [...actioned.unactioned, ...actioned.actioned],
    [actioned]
  );

  const { Header, Row, Footer, grid } = groupAssets;
  const equipCount = getEquipsCount(occurrences, showSnoozed);
  const hitCount = getHitsCount(occurrences, showSnoozed);
  const remainingCount = totalCount - currentCount;
  const moreCount =
    remainingCount > DEFAULT_LOAD_COUNT ? DEFAULT_LOAD_COUNT : remainingCount;

  if (actioned.actioned.length === 0 && actioned.unactioned.length === 0) {
    return null;
  }

  return (
    <Grid
      columns={grid.columns}
      group={grid.group}
      hide={grid.hide}
      grow={grid.grow}
      cellProps={{ type: "dense", density: "default" }}
    >
      <DataGridHeader cursor="pointer">
        <Header
          activeSort={activeSort}
          sortDirection={sortDirection}
          groupCount={totalCount}
          equipCount={equipCount}
          hitCount={hitCount}
          onSort={onSort(activeSort, true)}
        />
      </DataGridHeader>
      {rows.slice(0, currentCount).map((occurrence) => {
        const renderRow = showSnoozed || occurrence.activeHits > 0;
        return renderRow ? (
          <GoToEquip
            key={occurrence.id}
            to={{
              pathname: `/analysis/${viewMode}/${occurrence.site.syncoId}/${occurrence.ruleId}`,
            }}
            data-cy={occurrence.ruleName}
            className={
              occurrence.actionsCount === occurrence.targetCount
                ? "compact-density-row"
                : "default-density-row"
            }
          >
            <DataGridRow
              cursor="pointer"
              density={
                occurrence.actionsCount === occurrence.targetCount
                  ? "compact"
                  : "default"
              }
            >
              <Row showSnoozed={!!showSnoozed} {...occurrence} />
            </DataGridRow>
          </GoToEquip>
        ) : null;
      })}
      <StyledDataGridFooter>
        <Footer
          moreCount={moreCount}
          currentCount={currentCount}
          totalCount={totalCount}
          onLoadMore={() => setCurrentCount(currentCount + moreCount)}
          onLoadAll={() => setCurrentCount(totalCount)}
          exportData={occurrences}
          viewMode={viewMode}
        />
      </StyledDataGridFooter>
    </Grid>
  );
};

export default GroupGrid;
